import type { CertificateTemplateOption } from '~/gql/certificates/certificates'
import type { PickerGroup, PickerUser } from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'
import type { EditableSession } from '~/pages/backoffice/admin/sessions/editable-session'
import type { PathChapter } from '~/pages/backoffice/admin/sessions/path-chapters'
import type { PathStep, ReadyModule } from '~/pages/backoffice/admin/sessions/path-step'
import { useMutation } from '@apollo/client/react'
import { SessionProgressionMode, SessionStatus } from '@zeus/index'
import { Loader } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import FormWarning from '~/components/FormWarning'
import { SidePanel } from '~/components/SidePanel'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { GQL_CREATE_SESSION, GQL_UPDATE_SESSION } from '~/gql/sessions/sessions'
import { useBranding } from '~/helpers/branding/branding-context'
import { toDayInput } from '~/helpers/dates/session-dates'
import { sheetFormFrom, toSheetInput } from '~/helpers/sessions/training-sheet'
import { certificateRuleFrom, certificateWarnings, suggestedCertificateScore, toCertificateInput } from '~/pages/backoffice/admin/sessions/certificate-rule-form'
import CertificateConditionFields from '~/pages/backoffice/admin/sessions/components/CertificateConditionFields'
import CertificateTemplateField from '~/pages/backoffice/admin/sessions/components/CertificateTemplateField'
import ModulePathBuilder from '~/pages/backoffice/admin/sessions/components/ModulePathBuilder'
import ParticipantPicker from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'
import SessionTrainersField from '~/pages/backoffice/admin/sessions/components/SessionTrainersField'
import TagsField from '~/pages/backoffice/admin/sessions/components/TagsField'
import TrainingSheetFields from '~/pages/backoffice/admin/sessions/components/TrainingSheetFields'
import { chaptersFrom } from '~/pages/backoffice/admin/sessions/path-chapters'
import { sessionFormError } from '~/pages/backoffice/admin/sessions/session-form-error'
import { pathStepsFrom, toDayOrNull, toPathInput, toSectionsInput } from '~/pages/backoffice/admin/sessions/session-path-form'
import { SESSION_STATUS_KEYS } from '~/pages/backoffice/admin/sessions/session-status'

type IProps = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void | Promise<unknown>
  modules: ReadyModule[]
  /** Comptes de l'organisation : participants possibles, et formateurs parmi eux (#244). */
  users: (PickerUser & { role?: { name: string } | null })[]
  /** Groupes proposés en raccourci d'inscription (#135). */
  groups: PickerGroup[]
  /** Parcours à éditer ; absent = création. */
  session?: EditableSession
  /** Étiquettes déjà en usage dans l'organisation (#29), proposées à la saisie. */
  tagSuggestions: readonly string[]
  /** Modèles d'attestation de l'organisation (#162). */
  certificateTemplates: CertificateTemplateOption[]
}

function SessionModal({ open, onCancel, onSuccess, modules, users, groups, session, tagSuggestions, certificateTemplates }: IProps) {
  const { t } = useTranslation()
  const { timezone } = useBranding()
  const isEdit = !!session

  const [name, setName] = useState(session?.name ?? '')
  const [startDate, setStartDate] = useState(() => toDayInput(session?.startDate, timezone))
  const [endDate, setEndDate] = useState(() => toDayInput(session?.endDate, timezone))
  const [progressionMode, setProgressionMode] = useState<SessionProgressionMode>(
    (session?.progressionMode as SessionProgressionMode) ?? SessionProgressionMode.FREE,
  )
  const [status, setStatus] = useState<SessionStatus>(
    (session?.status as SessionStatus) ?? SessionStatus.ACTIVE,
  )
  const [steps, setSteps] = useState<PathStep[]>(
    () => pathStepsFrom(session?.modules ?? [], value => toDayInput(value, timezone)),
  )
  const [chapters, setChapters] = useState<PathChapter[]>(
    // Sur la même liste que `pathStepsFrom` (modules sans `module` écartés) :
    // sinon les positions des chapitres se décaleraient d'autant.
    () => chaptersFrom((session?.modules ?? []).filter(sm => sm.module?.id), session?.sections ?? []),
  )
  const [certificate, setCertificate] = useState(() => certificateRuleFrom(session))
  // '' = le modèle par défaut de l'organisation (#162).
  const [certificateTemplateId, setCertificateTemplateId] = useState(session?.certificateTemplateId ?? '')
  const [tags, setTags] = useState(() => session?.tags?.map(tag => tag.name) ?? [])
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(session?.enrolledUsers?.map(u => u.id) ?? []),
  )
  const [trainerIds, setTrainerIds] = useState(() => new Set(session?.trainers?.map(u => u.id) ?? []))
  const [sheet, setSheet] = useState(() => sheetFormFrom(session))

  const [createSession, { loading: creating }] = useMutation(GQL_CREATE_SESSION)
  const [updateSession, { loading: updating }] = useMutation(GQL_UPDATE_SESSION)
  const loading = creating || updating

  // Modules retirés du parcours en cours d'édition. Leur progression n'est PAS
  // effacée (elle vit dans ScormTracking, sans clé étrangère vers la jointure),
  // mais elle cesse de compter dans l'avancement — et un apprenant qui avait
  // terminé le parcours y repasse « en cours ». Ça se dit avant de valider.
  const initialModuleIds = (session?.modules ?? []).map(sm => sm.module?.id).filter(Boolean)
  const removed = initialModuleIds.filter(id => !steps.some(s => s.moduleId === id))
  const added = steps.filter(s => !initialModuleIds.includes(s.moduleId))
  const enrolledCount = selected.size
  const warnsAboutProgress = isEdit && enrolledCount > 0 && (removed.length > 0 || added.length > 0)
  const certificateWarning = certificateWarnings({
    isEdit,
    enrolledCount,
    stored: { rule: certificateRuleFrom(session), steps: pathStepsFrom(session?.modules ?? [], value => toDayInput(value, timezone)) },
    current: { rule: certificate, steps },
  })

  const handleSubmit = async () => {
    const error = sessionFormError({ name, steps, chapters, certificate })
    if (error) {
      toast.error(t(error))
      return
    }

    const pathInput = toPathInput(steps, chapters)
    const sectionsInput = toSectionsInput(chapters)

    // Mêmes champs à la création et à la modification ; seul le statut est propre
    // à un parcours qui existe déjà.
    const input = {
      name: name.trim(),
      progressionMode,
      ...toCertificateInput(certificate),
      certificateTemplateId: certificateTemplateId || null,
      tags,
      modules: pathInput,
      sections: sectionsInput,
      userIds: [...selected],
      trainerIds: [...trainerIds],
      sheet: toSheetInput(sheet),
      startDate: toDayOrNull(startDate),
      endDate: toDayOrNull(endDate),
    }

    try {
      if (isEdit) {
        await updateSession({ variables: { id: session.id, input: { ...input, status } } })
        toast.success(t('sessions.updated'))
      } else {
        await createSession({ variables: { input } })
        toast.success(t('sessions.created'))
      }
      await onSuccess()
      onCancel()
    } catch (error) {
      // Le message de l'API porte la raison exacte du refus (date d'ouverture
      // après la fin du parcours, module en double…) : la remplacer par un
      // « échec » générique obligerait l'admin à deviner ce qu'il a mal saisi.
      const message = error instanceof Error && error.message
        ? error.message
        : t(isEdit ? 'sessions.updateFailed' : 'sessions.createFailed')
      toast.error(message)
    }
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onCancel}
      size="lg"
      title={isEdit ? t('sessions.update.title') : t('sessions.create.title')}
      footer={(
        <>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader className="mr-2 size-4 animate-spin" />}
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4 px-1">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="session-name">{t('common.name')}</label>
            <Input id="session-name" value={name} onChange={e => setName(e.target.value)} placeholder={t('sessions.namePlaceholder')} />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="session-progression">
              {t('sessions.progression.label')}
            </label>
            <select
              id="session-progression"
              value={progressionMode}
              onChange={e => setProgressionMode(e.target.value as SessionProgressionMode)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value={SessionProgressionMode.FREE}>{t('sessions.progression.free')}</option>
              <option value={SessionProgressionMode.SEQUENTIAL}>{t('sessions.progression.sequential')}</option>
            </select>
            <p className="text-xs text-muted-foreground">
              {progressionMode === SessionProgressionMode.SEQUENTIAL
                ? t('sessions.progression.sequential.hint')
                : t('sessions.progression.free.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="session-start">{t('sessions.startDate')}</label>
            <Input id="session-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="session-end">{t('sessions.endDate')}</label>
            <Input id="session-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>

          {isEdit && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="session-status">{t('common.status')}</label>
              <select
                id="session-status"
                value={status}
                onChange={e => setStatus(e.target.value as SessionStatus)}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                {Object.entries(SESSION_STATUS_KEYS).map(([value, key]) => (
                  <option key={value} value={value}>{t(key)}</option>
                ))}
              </select>
            </div>
          )}

          <TagsField value={tags} onChange={setTags} suggestions={tagSuggestions} />

          <CertificateConditionFields
            value={certificate}
            onChange={setCertificate}
            suggestedMinScore={suggestedCertificateScore(steps, modules)}
          />

          <CertificateTemplateField value={certificateTemplateId} onChange={setCertificateTemplateId} templates={certificateTemplates} />
        </div>

        <TrainingSheetFields value={sheet} onChange={setSheet} />

        <ModulePathBuilder
          modules={modules}
          value={steps}
          onChange={setSteps}
          startDate={startDate}
          endDate={endDate}
          progressionMode={progressionMode}
          chapters={chapters}
          onChaptersChange={setChapters}
        />

        {warnsAboutProgress && <FormWarning>{t('sessions.path.warn.inProgress', { count: enrolledCount })}</FormWarning>}
        {certificateWarning.mayRevoke && <FormWarning>{t('sessions.certificate.warn', { count: enrolledCount })}</FormWarning>}
        {certificateWarning.noRetake && <FormWarning>{t('sessions.certificate.noRetake')}</FormWarning>}

        <SessionTrainersField users={users} assigned={session?.trainers} value={trainerIds} onChange={setTrainerIds} />

        <ParticipantPicker users={users} groups={groups} value={selected} onChange={setSelected} />
      </div>
    </SidePanel>
  )
}

export default SessionModal
